import {
  loadDraftCanvasView,
  loadDraftProject,
  loadPublishedCanvasView,
  loadPublishedProject,
} from "./canvasApi";
import {
  canvasPerfElapsedMs,
  canvasPerfNow,
  logCanvasPerf,
} from "./canvasPerf";

export function adaptCanvasViewProject(canvasView) {
  if (!canvasView) return canvasView;

  return {
    ...canvasView,
    canvasViewShell: true,
    components: (canvasView.components || []).map((component) => ({
      ...component,
      isCanvasViewShell: true,
      componentData: undefined,
      rawComponentData: undefined,
    })),
  };
}

function isErrorResponse(response) {
  return (
    response instanceof Error ||
    response?.response?.status >= 400 ||
    response?.status >= 400 ||
    !response?.data
  );
}

function loadFullCanvasProject(projectId, production) {
  return production
    ? loadPublishedProject(projectId)
    : loadDraftProject(projectId);
}

export async function loadInitialCanvasProject(
  projectId,
  { production = false, lightweightEnabled = false } = {},
) {
  const startedAt = canvasPerfNow();
  const mode = production ? "published" : "draft";
  if (!lightweightEnabled) {
    const response = await loadFullCanvasProject(projectId, production);
    logCanvasPerf("initial-canvas-api", {
      projectId,
      mode,
      path: "full-canvas",
      durationMs: canvasPerfElapsedMs(startedAt),
      components: response?.data?.components?.length ?? 0,
      canvasViewShell: false,
    });
    return response;
  }

  try {
    const response = production
      ? await loadPublishedCanvasView(projectId)
      : await loadDraftCanvasView(projectId);
    if (isErrorResponse(response)) {
      throw response;
    }

    const adapted = adaptCanvasViewProject(response?.data);
    logCanvasPerf("initial-canvas-api", {
      projectId,
      mode,
      path: "canvas-view",
      durationMs: canvasPerfElapsedMs(startedAt),
      components: adapted?.components?.length ?? 0,
      canvasViewShell: true,
    });
    return {
      ...response,
      data: adapted,
    };
  } catch (error) {
    console.warn(
      "[canvas-view] lightweight load failed; falling back to full canvas",
      error,
    );
    const fallbackStartedAt = canvasPerfNow();
    const response = await loadFullCanvasProject(projectId, production);
    logCanvasPerf("initial-canvas-api", {
      projectId,
      mode,
      path: "full-canvas-fallback",
      durationMs: canvasPerfElapsedMs(startedAt),
      fallbackDurationMs: canvasPerfElapsedMs(fallbackStartedAt),
      components: response?.data?.components?.length ?? 0,
      canvasViewShell: false,
    });
    return response;
  }
}
